import { useRoute, useRouter } from 'vue-router'
import { computed } from 'vue'
import { trackEvent } from '@/services/analytics-service'
import { useCoBrandedCardsAndSubscribedBonuses } from './coBrandPartnersAndSubscribedBonuses'
import { useUserStore } from '@/stores/user'
import { storeToRefs } from 'pinia'
import type { MenuEntry } from '@/types/navigation'
import { buildB2cMenu } from '@/helpers/navigation/b2c'
import { buildB2bMenu } from '@/helpers/navigation/b2b'

export { type MenuEntry }

// recursively filters out entries (and their children) that should not be displayed based on their `display` function.
const filterDisplayableEntries = (entries: MenuEntry[]): MenuEntry[] => {
  return entries.flatMap((entry) => {
    if (entry.display !== undefined && !entry.display()) return []
    const children = entry.children ? filterDisplayableEntries(entry.children) : undefined
    if (children?.length === 0) return []
    return [{ ...entry, children }]
  })
}

export const useNavigation = () => {
  const route = useRoute()
  const router = useRouter()
  const userStore = useUserStore()
  const { getCoBrandedPartners } = useCoBrandedCardsAndSubscribedBonuses()

  const { coBrandedCards, hasAutoConversion, userHasAtLeastOneSubscriptionCard } =
    storeToRefs(userStore)
  const hasAirlineOrAmexPartner = computed(
    () => getCoBrandedPartners(coBrandedCards.value).length > 0
  )

  const isB2B = computed(() => route.path.includes('/aab/'))

  const items = computed(() => {
    const menu = isB2B.value
      ? buildB2bMenu()
      : buildB2cMenu({
          hasSubscriptionCard: userHasAtLeastOneSubscriptionCard.value,
          hasCoBrandAirline: hasAirlineOrAmexPartner.value,
          hasAutoConversion: hasAutoConversion.value
        })
    return filterDisplayableEntries(menu)
  })

  const selectedItemId = computed(() => {
    const menuRouteNames = new Set(
      items.value.flatMap((g) => [g.routeName, ...(g.children?.map((c) => c.routeName) ?? [])])
    )
    const matched = route.matched.map((r) => r.name as string).find((n) => menuRouteNames.has(n))
    return matched ?? (route.name as string)
  })

  const updateSelectedItem = (entry: MenuEntry) => {
    if (entry.tracking) {
      trackEvent('GTM_MENU_ITEM', entry.tracking)
    }

    if (entry.url) {
      window.open(entry.url, '_blank')
    } else if (entry.routeName) {
      router.push({ name: entry.routeName })
    }
  }

  return {
    items,
    isB2B,
    selectedItemId,
    updateSelectedItem
  }
}
